import {
  Stack,
  Typography,
  Divider,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from 'helpers/hooks';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShButton } from 'shared/SharedStyles/ShInputs';
import { ShPaper } from 'shared/SharedStyles/ShSurfaces';
import { getJobDetails } from 'store/slices/employer/jobs/job-info-slice';
import { fetchEmailCampaignsByJob, createEmailCampaign, deleteEmailCampaign, updateEmailCampaign, sendEmailCampaign } from 'store/slices/employer/employer/email-campaigns-slice';
import EmailIcon from '@mui/icons-material/Email';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { StyledActionButton } from '../Applicants/ApplicantsList/ApplicantsToolBar.styles';
import { EmailCampaignTable } from './EmailCampaignTable';
import { EmailCampaignDialog } from './EmailCampaignDialog';
import { Campaign, EmailCampaignDialogValues } from './EmailCampaignModels';
import { ShDialog } from 'shared/SharedStyles/ShFeedback';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Chip from '@mui/material/Chip';
import { fetchApplicantsList } from 'store/slices/employer/applicants/applicants-list-slice';

export const EmailCampaignPage = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const { jobDetailsInInfo } = useAppSelector((state) => state.employer.employerJobs.jobInfo);
  const { campaigns, fetchCampaignsStatus, createCampaignStatus } = useAppSelector((state) => state.employer.emailCampaigns as { campaigns: Campaign[], fetchCampaignsStatus: string, createCampaignStatus: string });
  const dispatch = useAppDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<Campaign | null>(null);
  const [sendNowDialogOpen, setSendNowDialogOpen] = useState(false);
  const [campaignToSend, setCampaignToSend] = useState<Campaign | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [sendNowLoading, setSendNowLoading] = useState(false);
  const { applicantsList, getApplicantsListStatus } = useAppSelector((state) => state.employer.applicants.applicantsList);
  const [showAllRecipients, setShowAllRecipients] = useState(false);

  useEffect(() => {
    if (jobId !== undefined && !isNaN(parseInt(jobId))) {
      const id = parseInt(jobId);
      if (!jobDetailsInInfo) {
        dispatch(getJobDetails({ jobId: id }));
      }
      dispatch(fetchEmailCampaignsByJob({ jobId: id }));
      dispatch(fetchApplicantsList({
        jobId: id,
        pgNo: 1,
        pgSize: 1000,
        sortCol: 'created_at',
        sortDir: 'desc'
      }));
    }
  }, [dispatch, jobId, jobDetailsInInfo]);

  useEffect(() => {
    if (previewOpen && jobId) {
      dispatch(fetchApplicantsList({
        jobId: Number(jobId),
        pgNo: 1,
        pgSize: 1000,
        sortCol: 'created_at',
        sortDir: 'desc'
      }));
    }
  }, [previewOpen, jobId, dispatch]);

  const handleRowClick = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setPreviewOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedCampaign(null);
  };

  const handleDeleteClick = (id: number) => {
    const campaign = campaigns.find(c => c.id === id) || null;
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (campaignToDelete) {
      dispatch(deleteEmailCampaign({ id: campaignToDelete.id }));
    }
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  const handleSendNowClick = (id: number) => {
    const campaign = campaigns.find(c => c.id === id) || null;
    setCampaignToSend(campaign);
    setSendNowDialogOpen(true);
  };

  const handleConfirmSendNow = async () => {
    if (campaignToSend) {
      setSendNowLoading(true);
      await dispatch(sendEmailCampaign({ id: campaignToSend.id }));
      if (jobId) {
        dispatch(fetchEmailCampaignsByJob({ jobId }));
      }
      setSendNowLoading(false);
    }
    setSendNowDialogOpen(false);
    setCampaignToSend(null);
  };

  const handleCancelSendNow = () => {
    setSendNowDialogOpen(false);
    setCampaignToSend(null);
  };

  const handleEditClick = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingCampaign(null);
  };

  const handleSaveCampaign = (values: EmailCampaignDialogValues) => {
    if (!jobId) return;
    if (editMode && editingCampaign) {
      dispatch(updateEmailCampaign({
        id: editingCampaign.id,
        data: {
          name: values.title,
          subject: values.subject,
          previewText: values.preview,
          fromEmail: values.fromEmail,
          replyTo: values.replyTo,
          bcc: values.bcc,
          cc: values.cc,
          emailBody: values.emailBody,
          to: values.to,
          type: 'Regular',
          status: editingCampaign.status,
          recipientsCount: editingCampaign.recipientsCount,
          openRate: editingCampaign.openRate,
          clickRate: editingCampaign.clickRate,
          unsubscribersCount: editingCampaign.unsubscribersCount,
          createdAt: editingCampaign.createdAt,
        }
      }));
    } else {
      dispatch(createEmailCampaign({
        jobId,
        data: {
          name: values.title,
          subject: values.subject,
          previewText: values.preview,
          fromEmail: values.fromEmail,
          replyTo: values.replyTo,
          bcc: values.bcc,
          cc: values.cc,
          emailBody: values.emailBody,
          to: values.to,
          type: 'Regular',
          status: 'Draft',
          recipientsCount: 0,
          openRate: '-',
          clickRate: '-',
          unsubscribersCount: 0,
          createdAt: new Date().toISOString(),
        }
      }));
    }
    setDialogOpen(false);
    setEditMode(false);
    setEditingCampaign(null);
  };

  const getMatchedRecipients = (to: string[] | undefined) => {
    if (!to || !Array.isArray(to) || to.length === 0) {
      return applicantsList.map((a: { email: string }) => a.email);
    }
    return to.filter(email => applicantsList.some((a: { email: string }) => a.email === email));
  };

  const getRecipientChips = (to: string[] | undefined) => {
    const matched = getMatchedRecipients(to);
    if (!matched.length) return null;
    const chips = matched.map((email: string, idx: number) => {
      const applicant = applicantsList?.find((a: { email: string; fullName?: string }) => a.email === email);
      return <Chip key={email + idx} label={applicant?.fullName || email} size="small" sx={{ mr: 0.5, mb: 0.5 }} />;
    });
    if (chips.length > 10 && !showAllRecipients) {
      return <>
        {chips.slice(0, 10)}
        <Chip label={`+${chips.length - 10} more`} size="small" onClick={() => setShowAllRecipients(true)} sx={{ cursor: 'pointer' }} />
      </>;
    }
    return chips;
  };

  return (
    <ShPaper variant='outlined'>
        <ShButton disableElevation size='small' variant="contained" onClick={() => navigate(-1)} startIcon={<ArrowBackIcon />}>
            Back to Applicants
        </ShButton>
        <Stack pt={2}>
        <ShPaper variant='outlined' headerSection borderRadius={0}>
            <Typography variant="body2" gutterBottom>
                Email Campaign for {jobDetailsInInfo?.name}
            </Typography>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" mb={1}>
                {jobDetailsInInfo?.employer_company_name && (
                <Typography variant="subtitle2" color="text.secondary">
                    Company: {jobDetailsInInfo.employer_company_name}
                </Typography>
                )}
                {jobDetailsInInfo?.city && (
                <Typography variant="subtitle2" color="text.secondary">
                    Location: {jobDetailsInInfo.city}
                </Typography>
                )}
                {jobDetailsInInfo?.department && (
                <Typography variant="subtitle2" color="text.secondary">
                    Department: {jobDetailsInInfo.department}
                </Typography>
                )}
            </Stack>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Job ID: {jobId}
            </Typography>
        </ShPaper>
        <Grid container spacing={1} alignItems="stretch" pt={1} pb={1}>
            <Grid item xs={12} md={6}>
                <ShPaper variant="outlined">
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <CampaignOutlinedIcon color="primary" sx={{ fontSize: 40 }} />
                            <Typography variant="body2">Email Campaigns</Typography>
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                            Send mass emails to your candidates to keep them up to date, share important updates, or engage them with your hiring process. Easily create, manage, and track your campaigns here.
                        </Typography>
                        <Stack spacing={2}>
                            <Typography
                                variant="body2"
                                component="a"
                                href="/employer/help/email-campaign"
                                target="_blank"
                                rel="noopener noreferrer"
                                color="primary"
                                sx={{ display: 'inline-flex', alignItems: 'center', width: 'fit-content', textDecoration: 'underline', cursor: 'pointer', fontWeight: 500 }}
                            >
                                Learn More <OpenInNewIcon sx={{ fontSize: 18, ml: 0.5 }} />
                            </Typography>
                            <ShButton variant="contained" color="primary" size="small" disableElevation startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                                Create Campaign
                            </ShButton>
                        </Stack>
                    </Stack>
                </ShPaper>
            </Grid>
            <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                    <ShPaper variant="outlined">
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <LightbulbOutlinedIcon color="warning" sx={{ fontSize: 40}} />
                            <Box>
                            <Typography variant="body2" gutterBottom>
                                Best Practices
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Personalize your emails for better engagement. Keep your content relevant and concise. Schedule campaigns for optimal times to maximize open rates.
                            </Typography>
                            </Box>
                        </Stack>
                    </ShPaper>
                    <ShPaper variant="outlined">
                        <Stack direction="row" spacing={2} alignItems="flex-start">
                            <InfoOutlinedIcon color="info" sx={{ fontSize: 40}} />
                            <Box>
                              <Typography variant="body2" gutterBottom>
                                  Compliance Reminder
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                  Always comply with anti-spam laws. Communicate professionally and treat candidate data with care. Any misuse of this feature will result in account suspension.
                              </Typography>
                            </Box>
                        </Stack>
                    </ShPaper>
                </Stack>
            </Grid>
        </Grid>
        </Stack>
        <ShPaper variant='outlined' noBorderTop>
            <Grid container spacing={2} justifyContent="center">
                <Grid item xs={12} sm={4}>
                    <ShPaper variant="outlined">
                        <Stack flexGrow={1}>
                            <Box textAlign="center" p={3}>
                                <EmailIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                    Regular Email Campaign
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Create a single email campaign.
                                </Typography>
                                <StyledActionButton size='small' variant="outlined" color="primary" onClick={() => setDialogOpen(true)} startIcon={<AddIcon />}>Create Campaign</StyledActionButton>
                            </Box>
                        </Stack>
                    </ShPaper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ShPaper variant="outlined">
                        <Stack flexGrow={1}>
                            <Box textAlign="center" p={3}>
                                <AutoAwesomeMotionIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                    Email Sequence Campaign (Coming Soon)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Send a series of emails with time intervals.
                                </Typography>
                                <StyledActionButton variant="outlined" disabled>Create Sequence</StyledActionButton>
                            </Box>
                        </Stack>
                    </ShPaper>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <ShPaper variant="outlined">
                        <Stack flexGrow={1}>
                            <Box textAlign="center" p={3}>
                                <AutorenewIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
                                <Typography variant="subtitle2" gutterBottom>
                                    Sequence For Automation (Coming Soon)
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    Set up an email sequence to reuse for automation.
                                </Typography>
                                <StyledActionButton variant="outlined" disabled>Prepare Sequence</StyledActionButton>
                            </Box>
                        </Stack>
                    </ShPaper>
                </Grid>
            </Grid>
            <EmailCampaignTable jobId={jobId ?? ''} campaigns={campaigns} onPreview={handleRowClick} sendNowButtonProps={{ startIcon: <SendIcon /> }} fetchCampaignsStatus={fetchCampaignsStatus} onDelete={handleDeleteClick} onSendNowConfirm={handleSendNowClick} onEdit={handleEditClick} />
        </ShPaper>
        <EmailCampaignDialog
              open={dialogOpen}
              onClose={handleDialogClose}
              onSave={handleSaveCampaign}
              loading={createCampaignStatus === 'pending'}
              initialValues={editMode && editingCampaign ? {
                title: editingCampaign.name,
                to: editingCampaign.to || [],
                subject: editingCampaign.subject,
                preview: editingCampaign.previewText || '',
                fromEmail: editingCampaign.fromEmail || '',
                replyTo: editingCampaign.replyTo || '',
                bcc: editingCampaign.bcc || [],
                cc: editingCampaign.cc || [],
                emailBody: editingCampaign.emailBody || '',
              } : undefined}
            />

            <ShDialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Email Campaign Preview
                <ShButton onClick={handleClosePreview} size="small" color="inherit" sx={{ minWidth: 0 }}>
                    <CloseIcon />
                </ShButton>
            </DialogTitle>
            <DialogContent>
                {selectedCampaign && (
                <Stack spacing={2} mt={1}>
                    <Typography variant="subtitle2">Name: {selectedCampaign.name}</Typography>
                    <Typography variant="subtitle2">Subject: {selectedCampaign.subject}</Typography>
                    <Typography variant="subtitle2">
                        Recipients ({getMatchedRecipients(selectedCampaign.to).length} of {applicantsList.length} applicants):
                    </Typography>
                    {getApplicantsListStatus === 'pending' ? (
                      <Typography>Loading recipients...</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', mb: 1 }}>
                        {getRecipientChips(selectedCampaign.to)}
                      </Box>
                    )}
                    <Typography variant="subtitle2">From: {selectedCampaign.fromEmail || '-'}</Typography>
                    <Typography variant="subtitle2">Reply To: {selectedCampaign.replyTo || '-'}</Typography>
                    <Typography variant="subtitle2">BCC: {selectedCampaign.bcc?.join(', ') || '-'}</Typography>
                    <Typography variant="subtitle2">CC: {selectedCampaign.cc?.join(', ') || '-'}</Typography>
                    <Divider flexItem />
                    <Typography variant="subtitle2">Email Body:</Typography>
                    <Box p={2} sx={{ border: '1px solid #eee', borderRadius: 2, background: '#fafbfc' }}>
                    <div dangerouslySetInnerHTML={{ __html: selectedCampaign.emailBody || '' }} />
                    </Box>
                </Stack>
                )}
            </DialogContent>
            </ShDialog>

            <ShDialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Delete Campaign
                <ShButton onClick={handleCancelDelete} size="small" color="inherit" sx={{ minWidth: 0 }}>
                    <CloseIcon />
                </ShButton>
            </DialogTitle>
            <DialogContent>
                <Typography>Are you sure you want to delete the campaign <b>{campaignToDelete?.name}</b> with subject <b>{campaignToDelete?.subject}</b>? This action cannot be undone.</Typography>
            </DialogContent>
            <DialogActions>
                <ShButton size='small' disableElevation onClick={handleCancelDelete} startIcon={<CloseIcon />}>Cancel</ShButton>
                <ShButton size='small' disableElevation onClick={handleConfirmDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>Delete</ShButton>
            </DialogActions>
            </ShDialog>

            <ShDialog open={sendNowDialogOpen} onClose={handleCancelSendNow} maxWidth="xs">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Send Campaign
                <ShButton onClick={handleCancelSendNow} size="small" color="inherit" sx={{ minWidth: 0 }}>
                    <CloseIcon />
                </ShButton>
            </DialogTitle>
            <DialogContent>
                <Typography>
                  Are you sure you want to send the campaign <b>{campaignToSend?.name}</b> with subject <b>{campaignToSend?.subject}</b> now to <b>{
                    !campaignToSend?.to || campaignToSend.to.length === 0
                      ? applicantsList.length
                      : campaignToSend.to.filter(email => applicantsList.some((a: { email: string }) => a.email === email)).length
                  }</b> recipients?
                </Typography>
            </DialogContent>
            <DialogActions>
                <ShButton size='small' disableElevation onClick={handleCancelSendNow} startIcon={<CloseIcon />}>Cancel</ShButton>
                <ShButton size='small' disableElevation onClick={handleConfirmSendNow} color="primary" variant="contained" startIcon={<SendIcon />} disabled={sendNowLoading}>
                {sendNowLoading ? 'Sending...' : 'Send Now'}
                </ShButton>
            </DialogActions>
            </ShDialog>
    </ShPaper>
  );
}; 